/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import CatalogView, { CatalogSkeleton, CatalogUnavailable } from "../../components/catalog/CatalogView.jsx";
import CatalogItemViewer from "../../components/catalog/CatalogItemViewer.jsx";
import { itemEnquiryText, whatsappUrl } from "../../components/catalog/catalogUtils.js";

const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;

/** Lee el catálogo público. `preview` no cuenta la visita (relecturas de la propia web). */
function useCatalog(token, { preview = false } = {}) {
    const [state, setState] = useState({ loading: true, error: null, catalog: null });
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setState({ loading: true, error: null, catalog: null });
        axios.get(`catalog/${token}/`, { params: preview ? { preview: 1 } : undefined })
            .then(({ data }) => { if (!cancelled) setState({ loading: false, error: null, catalog: data }); })
            .catch((error) => {
                if (cancelled) return;
                setState({ loading: false, error: error?.response?.status === 404 ? "not_found" : "network", catalog: null });
            });
        return () => { cancelled = true; };
    }, [token, preview, attempt]);

    return { ...state, retry: () => setAttempt((value) => value + 1) };
}

function CatalogError({ error, retry }) {
    if (error === "not_found") {
        return <CatalogUnavailable action={<Link to="/" className="btn btn-ghost btn-sm">Ir a Customiz3d</Link>} />;
    }
    return (
        <CatalogUnavailable
            title="No hemos podido cargar el catálogo"
            text="Revisa tu conexión e inténtalo otra vez."
            action={<button type="button" className="btn btn-primary btn-sm" onClick={retry}>Reintentar</button>}
        />
    );
}

/** `/catalogo/:token`: el catálogo que se manda por WhatsApp (C3D-38). */
export default function PublicCatalog() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { loading, error, catalog, retry } = useCatalog(token);

    // El PDF enlaza cada modelo como `?item=<id>`: se abre directo en 3D.
    const itemFromPdf = searchParams.get("item");
    useEffect(() => {
        if (itemFromPdf) navigate(`/catalogo/${token}/modelo/${itemFromPdf}`, { replace: true });
    }, [itemFromPdf, navigate, token]);

    if (loading) return <CatalogSkeleton />;
    if (error) return <CatalogError error={error} retry={retry} />;

    const pdfUrl = `${API_BASE_URL}catalog/${token}/pdf/?origin=${encodeURIComponent(window.location.origin)}`;
    return (
        <CatalogView
            catalog={catalog}
            pdfUrl={pdfUrl}
            onView3d={(item) => navigate(`/catalogo/${token}/modelo/${item.id}`)}
        />
    );
}

/** `/catalogo/:token/modelo/:itemId`: un modelo del catálogo en 3D. */
export function PublicCatalogModel() {
    const { token, itemId } = useParams();
    const navigate = useNavigate();
    const { loading, error, catalog, retry } = useCatalog(token, { preview: true });
    const back = () => navigate(`/catalogo/${token}`);

    if (loading) return <CatalogSkeleton />;
    if (error) return <CatalogError error={error} retry={retry} />;

    const item = catalog.items.find((candidate) => String(candidate.id) === String(itemId));
    if (!item || !item.has_3d) {
        return (
            <CatalogUnavailable
                title="Este modelo no se puede ver en 3D"
                text="Vuelve al catálogo para ver el resto de modelos."
                action={<button type="button" className="btn btn-primary btn-sm" onClick={back}>Volver al catálogo</button>}
            />
        );
    }

    return (
        <CatalogItemViewer
            glbUrl={`${API_BASE_URL}catalog/${token}/items/${item.id}/glb/`}
            title={item.title}
            onBack={back}
            backLabel="Volver al catálogo"
            enquiryUrl={whatsappUrl(catalog.contact?.whatsapp_phone, itemEnquiryText(catalog.name, item))}
        />
    );
}
